import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { List, Calendar, BarChart3, Plus, Download, ChevronLeft, ChevronRight, FileDown } from 'lucide-react';
import GanttChart from '../components/GanttChart';
import TaskDetailModal from '../components/TaskDetailModal';
import TaskManagerHeader from '../components/TaskManagerHeader';
import TaskFilters from '../components/TaskFilters';
import TaskList from '../components/TaskList';
import CreateTaskModal from '../components/CreateTaskModal';
import useTaskManagement from '../hooks/useTaskManagement';
import useTaskFilters from '../hooks/useTaskFilters';
import logger from '../utils/logger';
import { toLocalDateString } from '../utils/dateUtils';
import { useTimezone } from '../contexts/TimezoneContext';
import { usePermissions } from '../contexts/PermissionContext';
import ViewerBanner from '../components/ViewerBanner';

const TaskManager = () => {
  const location = useLocation();
  const { timezone } = useTimezone();
  const { isViewer } = usePermissions();

  // Use custom hooks for task management and filtering
  const {
    rawTasks,
    stats,
    loading,
    loadingMore,
    assessmentQuestions,
    fetchTasks,
    fetchStats,
    fetchAssessmentQuestions,
    createTask,
    updateTask,
    deleteTask,
  } = useTaskManagement();

  const {
    filter,
    priorityFilter,
    activityTypeFilter,
    intangibleCapitalFilter,
    dueDateFilter,
    gapScoreFilter,
    filterCategory,
    filterQuestionId,
    assessmentTypeFilter,
    sortBy,
    sortOrder,
    filteredTasks: tasks,
    setFilter,
    setPriorityFilter,
    setActivityTypeFilter,
    setIntangibleCapitalFilter,
    setDueDateFilter,
    setGapScoreFilter,
    setFilterCategory,
    setFilterQuestionId,
    setAssessmentTypeFilter,
    setSortBy,
    setSortOrder,
    resetFilters,
    getCurrentServerFilters,
  } = useTaskFilters(rawTasks);

  // View state
  const [viewMode, setViewMode] = useState('list'); // 'list', 'calendar', 'gantt'
  const [showFilters, setShowFilters] = useState(false);
  const [calendarPeriod, setCalendarPeriod] = useState('month'); // '5day', '7day', 'month', '3month', '6month'
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null); // Selected date for viewing tasks
  const [selectedTask, setSelectedTask] = useState(null); // Selected task for detail view
  const [showAddModal, setShowAddModal] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Handle navigation from "View Related Tasks" button - set filter state immediately
  useEffect(() => {
    if (location.state?.questionId && assessmentQuestions.length > 0) {
      logger.debug('Navigation: Setting question filter from location state', { questionId: location.state.questionId });
      setFilterQuestionId(location.state.questionId);
      setShowFilters(true);

      // Also set the category filter for UI display
      const question = assessmentQuestions.find(q => q.question_id === location.state.questionId);
      if (question) {
        logger.debug('Navigation: Setting category filter from question', { category: question.category });
        setFilterCategory(question.category);
      }

      // Set assessment type filter if provided
      if (location.state?.assessmentType) {
        logger.debug('Navigation: Setting assessment type filter', { assessmentType: location.state.assessmentType });
        setAssessmentTypeFilter(location.state.assessmentType);
      }
    }
  }, [location.state?.questionId, location.state?.assessmentType, assessmentQuestions, setFilterQuestionId, setFilterCategory, setAssessmentTypeFilter]);

  // Fetch data on mount with navigation filters if present
  useEffect(() => {
    logger.debug('TaskManager mounted', { locationState: location.state });

    const serverFilters = {};
    if (location.state?.questionId) {
      serverFilters.questionId = location.state.questionId;
      logger.debug('Mount: Fetching tasks with question filter', { questionId: location.state.questionId });
    }

    fetchTasks(1, false, false, serverFilters);
    fetchStats();
    fetchAssessmentQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Refetch when filters change (but not on mount)
  useEffect(() => {
    // Skip on initial mount - the mount effect handles that
    if (filterQuestionId === '' && filterCategory === '') {
      return;
    }

    logger.debug('Filter changed', { filterQuestionId, filterCategory });

    const serverFilters = {};
    if (filterQuestionId) {
      serverFilters.questionId = filterQuestionId;
    } else if (filterCategory && filterCategory !== 'all') {
      serverFilters.category = filterCategory;
    }

    logger.debug('Fetching tasks with updated filters', { serverFilters });
    fetchTasks(1, false, false, serverFilters);
  }, [filterCategory, filterQuestionId, fetchTasks]);

  // Task action handlers
  const handleCreateTask = async (taskData) => {
    const result = await createTask(taskData);
    if (result.success) {
      // Fetch tasks in background without blocking modal animation
      fetchTasks(1, false, false, getCurrentServerFilters()).then(() => {
        fetchStats();
      });
    }
    return result;
  };

  const handleTaskUpdate = async (taskId, updates) => {
    const result = await updateTask(taskId, updates);
    if (result.success) {
      await fetchStats();
    }
    return result;
  };

  const handleTaskDelete = async (taskId) => {
    const result = await deleteTask(taskId);
    if (result.success) {
      await fetchStats();
    }
    return result;
  };

  // Gantt chart task update handler
  const handleGanttTaskUpdate = async ({ taskId, updates }) => {
    return await handleTaskUpdate(taskId, updates);
  };

  // Reset filters handler
  const handleResetFilters = () => {
    resetFilters();
    // Manually trigger refetch with no filters since useEffect will skip when filters are empty
    fetchTasks(1, false, false, {});
  };

  // PDF Download Handler
  const handleDownloadPDF = async () => {
    // Get business ID from localStorage (same as API interceptor)
    const businessId = localStorage.getItem('currentBusinessId');

    if (!businessId) {
      alert('Business ID not found. Please ensure you are logged in and have a business profile.');
      return;
    }

    setDownloadingPDF(true);

    try {
      // Get auth token
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      // Build query parameters with all current filters and view settings
      const params = new URLSearchParams();
      params.append('view_mode', viewMode);

      // Add all filter parameters
      if (filter && filter !== 'all') params.append('filter', filter);
      if (priorityFilter && priorityFilter !== 'all') params.append('priorityFilter', priorityFilter);
      if (activityTypeFilter && activityTypeFilter !== 'all') params.append('activityTypeFilter', activityTypeFilter);
      if (intangibleCapitalFilter && intangibleCapitalFilter !== 'all') params.append('intangibleCapitalFilter', intangibleCapitalFilter);
      if (dueDateFilter && dueDateFilter !== 'all') params.append('dueDateFilter', dueDateFilter);
      if (gapScoreFilter && gapScoreFilter !== 'all') params.append('gapScoreFilter', gapScoreFilter);
      if (filterCategory && filterCategory !== '') params.append('filterCategory', filterCategory);
      if (filterQuestionId && filterQuestionId !== '') params.append('filterQuestionId', filterQuestionId);
      if (assessmentTypeFilter && assessmentTypeFilter !== 'all') params.append('assessmentTypeFilter', assessmentTypeFilter);
      if (sortBy) params.append('sortBy', sortBy);
      if (sortOrder) params.append('sortOrder', sortOrder);

      // Add calendar-specific parameters
      if (viewMode === 'calendar') {
        params.append('calendarPeriod', calendarPeriod);
        params.append('calendarDate', calendarDate.toISOString());
      }

      const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/pdf/tasks/${businessId}?${params.toString()}`;

      logger.debug('Downloading task PDF', { businessId, viewMode, filters: Object.fromEntries(params) });

      // Fetch PDF
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to generate PDF' }));
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      // Get filename from headers
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'tasks.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*?=["']?([^"';]+)["']?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
          filename = filename.replace(/^UTF-8''/, '');
          filename = decodeURIComponent(filename);
        }
      }

      logger.debug('Downloading PDF', { filename });

      // Download PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      logger.info('Task PDF downloaded successfully');
    } catch (err) {
      logger.error('Error downloading task PDF', { error: err.message });
      alert(`Failed to download PDF: ${err.message}`);
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Calendar Navigation Functions
  const navigateCalendar = (direction) => {
    const newDate = new Date(calendarDate);

    switch (calendarPeriod) {
      case '5day':
      case '7day':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
        break;
      case '3month':
        newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 3 : -3));
        break;
      case '6month':
        newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 6 : -6));
        break;
      default:
        break;
    }

    setCalendarDate(newDate);
  };

  // Get holidays and special dates for a given date
  const getHolidaysForDate = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth(); // 0-11
    const day = date.getDate();

    const holidays = [];

    // Fixed date holidays
    if (month === 0 && day === 1) holidays.push("New Year's Day");
    if (month === 1 && day === 2) holidays.push("Groundhog Day");
    if (month === 1 && day === 14) holidays.push("Valentine's Day");
    if (month === 2 && day === 17) holidays.push("St. Patrick's Day");
    if (month === 3 && day === 22) holidays.push("Earth Day");
    if (month === 4 && day === 5) holidays.push("Cinco de Mayo");
    if (month === 5 && day === 19) holidays.push("Juneteenth");
    if (month === 6 && day === 4) holidays.push("Independence Day");
    if (month === 9 && day === 31) holidays.push("Halloween");
    if (month === 10 && day === 11) holidays.push("Veterans Day");
    if (month === 11 && day === 24) holidays.push("Christmas Eve");
    if (month === 11 && day === 25) holidays.push("Christmas Day");
    if (month === 11 && day === 31) holidays.push("New Year's Eve");

    // MLK Day - Third Monday in January
    if (month === 0) {
      const firstDay = new Date(year, 0, 1);
      const firstMonday = 1 + ((8 - firstDay.getDay()) % 7);
      const thirdMonday = firstMonday + 14;
      if (day === thirdMonday) holidays.push("Martin Luther King Jr. Day");
    }

    // Presidents Day - Third Monday in February
    if (month === 1) {
      const firstDay = new Date(year, 1, 1);
      const firstMonday = 1 + ((8 - firstDay.getDay()) % 7);
      const thirdMonday = firstMonday + 14;
      if (day === thirdMonday) holidays.push("Presidents' Day");
    }

    // Memorial Day - Last Monday in May
    if (month === 4) {
      const lastDay = new Date(year, 5, 0).getDate();
      let lastMonday = lastDay;
      const lastDayOfWeek = new Date(year, 4, lastDay).getDay();
      lastMonday = lastDay - ((lastDayOfWeek + 6) % 7);
      if (day === lastMonday) holidays.push("Memorial Day");
    }

    // Labor Day - First Monday in September
    if (month === 8) {
      const firstDay = new Date(year, 8, 1);
      const firstMonday = 1 + ((8 - firstDay.getDay()) % 7);
      if (day === firstMonday) holidays.push("Labor Day");
    }

    // Columbus Day - Second Monday in October
    if (month === 9) {
      const firstDay = new Date(year, 9, 1);
      const firstMonday = 1 + ((8 - firstDay.getDay()) % 7);
      const secondMonday = firstMonday + 7;
      if (day === secondMonday) holidays.push("Columbus Day");
    }

    // Thanksgiving - Fourth Thursday in November
    if (month === 10) {
      const firstDay = new Date(year, 10, 1);
      const firstThursday = 1 + ((11 - firstDay.getDay()) % 7);
      const fourthThursday = firstThursday + 21;
      if (day === fourthThursday) holidays.push("Thanksgiving Day");
      if (day === fourthThursday + 1) holidays.push("Black Friday");
    }

    // Seasonal dates (approximate)
    if (month === 2 && day >= 19 && day <= 21) holidays.push("First Day of Spring");
    if (month === 5 && day >= 20 && day <= 22) holidays.push("First Day of Summer");
    if (month === 8 && day >= 21 && day <= 23) holidays.push("First Day of Fall");
    if (month === 11 && day >= 20 && day <= 22) holidays.push("First Day of Winter");

    // Mother's Day - Second Sunday in May
    if (month === 4) {
      const firstDay = new Date(year, 4, 1);
      const firstSunday = 1 + ((7 - firstDay.getDay()) % 7);
      const secondSunday = firstSunday + 7;
      if (day === secondSunday) holidays.push("Mother's Day");
    }

    // Father's Day - Third Sunday in June
    if (month === 5) {
      const firstDay = new Date(year, 5, 1);
      const firstSunday = 1 + ((7 - firstDay.getDay()) % 7);
      const thirdSunday = firstSunday + 14;
      if (day === thirdSunday) holidays.push("Father's Day");
    }

    return holidays;
  };

  // Calendar View Helper Functions
  const getCalendarData = () => {
    const today = new Date();
    const referenceDate = new Date(calendarDate);

    switch (calendarPeriod) {
      case '5day': {
        // Show 5-day work week (Mon-Fri)
        const currentDay = referenceDate.getDay();
        const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
        const startDate = new Date(referenceDate);
        startDate.setDate(startDate.getDate() + mondayOffset);

        const userTimezone = timezone;
        const days = [];
        for (let i = 0; i < 5; i++) {
          const currentDate = new Date(startDate);
          currentDate.setDate(startDate.getDate() + i);
          const dateStr = toLocalDateString(currentDate, userTimezone);
          const dayTasks = tasks.filter(task => task.due_date === dateStr);

          days.push({
            date: new Date(currentDate),
            dateStr: dateStr,
            isCurrentMonth: true,
            isToday: currentDate.toDateString() === today.toDateString(),
            tasks: dayTasks,
            holidays: getHolidaysForDate(currentDate)
          });
        }

        return {
          type: '5day',
          days: [days],
          title: `${startDate.toLocaleDateString('default', { month: 'short', day: 'numeric' })} - ${days[4].date.toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' })}`
        };
      }

      case '7day': {
        // Show 7-day week (Sun-Sat)
        const currentDay = referenceDate.getDay();
        const startDate = new Date(referenceDate);
        startDate.setDate(startDate.getDate() - currentDay);

        const userTimezone = timezone;
        const days = [];
        for (let i = 0; i < 7; i++) {
          const currentDate = new Date(startDate);
          currentDate.setDate(startDate.getDate() + i);
          const dateStr = toLocalDateString(currentDate, userTimezone);
          const dayTasks = tasks.filter(task => task.due_date === dateStr);

          days.push({
            date: new Date(currentDate),
            dateStr: dateStr,
            isCurrentMonth: true,
            isToday: currentDate.toDateString() === today.toDateString(),
            tasks: dayTasks,
            holidays: getHolidaysForDate(currentDate)
          });
        }

        return {
          type: '7day',
          days: [days],
          title: `${startDate.toLocaleDateString('default', { month: 'short', day: 'numeric' })} - ${days[6].date.toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' })}`
        };
      }

      case 'month': {
        const year = referenceDate.getFullYear();
        const month = referenceDate.getMonth();

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - firstDay.getDay());

        const userTimezone = timezone;
        const weeks = [];
        let currentDate = new Date(startDate);

        while (currentDate <= lastDay || currentDate.getDay() !== 0) {
          const week = [];
          for (let i = 0; i < 7; i++) {
            const dateStr = toLocalDateString(currentDate, userTimezone);
            const isCurrentMonth = currentDate.getMonth() === month;
            // Only show tasks for days in the current month
            const dayTasks = isCurrentMonth ? tasks.filter(task => task.due_date === dateStr) : [];

            week.push({
              date: new Date(currentDate),
              dateStr: dateStr,
              isCurrentMonth: isCurrentMonth,
              isToday: currentDate.toDateString() === today.toDateString(),
              tasks: dayTasks,
              holidays: getHolidaysForDate(currentDate)
            });

            currentDate.setDate(currentDate.getDate() + 1);
          }
          weeks.push(week);

          if (currentDate > lastDay && currentDate.getDay() === 0) break;
        }

        return {
          type: 'month',
          weeks: weeks,
          title: firstDay.toLocaleString('default', { month: 'long', year: 'numeric' })
        };
      }

      case '3month': {
        const startMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
        const userTimezone = timezone;
        const months = [];

        for (let m = 0; m < 3; m++) {
          const monthDate = new Date(startMonth);
          monthDate.setMonth(startMonth.getMonth() + m);

          const year = monthDate.getFullYear();
          const month = monthDate.getMonth();
          const firstDay = new Date(year, month, 1);
          const lastDay = new Date(year, month + 1, 0);

          const weeks = [];
          let currentDate = new Date(firstDay);
          currentDate.setDate(currentDate.getDate() - firstDay.getDay());

          while (currentDate <= lastDay || currentDate.getDay() !== 0) {
            const week = [];
            for (let i = 0; i < 7; i++) {
              const dateStr = toLocalDateString(currentDate, userTimezone);
              const isCurrentMonth = currentDate.getMonth() === month;
              // Only show tasks for days in the current month
              const dayTasks = isCurrentMonth ? tasks.filter(task => task.due_date === dateStr) : [];

              week.push({
                date: new Date(currentDate),
                dateStr: dateStr,
                isCurrentMonth: isCurrentMonth,
                isToday: currentDate.toDateString() === today.toDateString(),
                tasks: dayTasks,
                holidays: getHolidaysForDate(currentDate)
              });

              currentDate.setDate(currentDate.getDate() + 1);
            }
            weeks.push(week);

            if (currentDate > lastDay && currentDate.getDay() === 0) break;
          }

          months.push({
            weeks: weeks,
            title: firstDay.toLocaleString('default', { month: 'long', year: 'numeric' })
          });
        }

        return {
          type: '3month',
          months: months,
          title: `${months[0].title} - ${months[2].title}`
        };
      }

      case '6month': {
        const startMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
        const userTimezone = timezone;
        const months = [];

        for (let m = 0; m < 6; m++) {
          const monthDate = new Date(startMonth);
          monthDate.setMonth(startMonth.getMonth() + m);

          const year = monthDate.getFullYear();
          const month = monthDate.getMonth();
          const firstDay = new Date(year, month, 1);
          const lastDay = new Date(year, month + 1, 0);

          const weeks = [];
          let currentDate = new Date(firstDay);
          currentDate.setDate(currentDate.getDate() - firstDay.getDay());

          while (currentDate <= lastDay || currentDate.getDay() !== 0) {
            const week = [];
            for (let i = 0; i < 7; i++) {
              const dateStr = toLocalDateString(currentDate, userTimezone);
              const isCurrentMonth = currentDate.getMonth() === month;
              // Only show tasks for days in the current month
              const dayTasks = isCurrentMonth ? tasks.filter(task => task.due_date === dateStr) : [];

              week.push({
                date: new Date(currentDate),
                dateStr: dateStr,
                isCurrentMonth: isCurrentMonth,
                isToday: currentDate.toDateString() === today.toDateString(),
                tasks: dayTasks,
                holidays: getHolidaysForDate(currentDate)
              });

              currentDate.setDate(currentDate.getDate() + 1);
            }
            weeks.push(week);

            if (currentDate > lastDay && currentDate.getDay() === 0) break;
          }

          months.push({
            weeks: weeks,
            title: firstDay.toLocaleString('default', { month: 'short' })
          });
        }

        return {
          type: '6month',
          months: months,
          title: `${months[0].title} - ${months[5].title} ${referenceDate.getFullYear()}`
        };
      }

      default:
        return { type: 'month', weeks: [], title: '' };
    }
  };

  // Gantt View Helper Functions
  const getGanttData = () => {
    const tasksWithDates = tasks.filter(task => task.due_date || task.created_at);

    if (tasksWithDates.length === 0) return { tasks: [], startDate: new Date(), endDate: new Date(), totalDays: 0 };

    let minDate = new Date();
    let maxDate = new Date();

    tasksWithDates.forEach(task => {
      const createdDate = new Date(task.created_at);
      const dueDate = task.due_date ? new Date(task.due_date) : new Date(createdDate.getTime() + 30 * 24 * 60 * 60 * 1000);

      if (createdDate < minDate) minDate = createdDate;
      if (dueDate > maxDate) maxDate = dueDate;
    });

    const totalDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) + 1;

    return {
      tasks: tasksWithDates.map(task => {
        const createdDate = new Date(task.created_at);
        const dueDate = task.due_date ? new Date(task.due_date) : new Date(createdDate.getTime() + 30 * 24 * 60 * 60 * 1000);
        const startOffset = Math.ceil((createdDate - minDate) / (1000 * 60 * 60 * 24));
        const duration = Math.ceil((dueDate - createdDate) / (1000 * 60 * 60 * 24)) + 1;

        return {
          ...task,
          startDate: createdDate,
          endDate: dueDate,
          startOffset,
          duration
        };
      }),
      startDate: minDate,
      endDate: maxDate,
      totalDays
    };
  };

  // Filter change handler
  const handleFilterChange = (filterName, value) => {
    switch (filterName) {
      case 'filter':
        setFilter(value);
        break;
      case 'priorityFilter':
        setPriorityFilter(value);
        break;
      case 'activityTypeFilter':
        setActivityTypeFilter(value);
        break;
      case 'intangibleCapitalFilter':
        setIntangibleCapitalFilter(value);
        break;
      case 'dueDateFilter':
        setDueDateFilter(value);
        break;
      case 'gapScoreFilter':
        setGapScoreFilter(value);
        break;
      case 'assessmentTypeFilter':
        setAssessmentTypeFilter(value);
        break;
      case 'filterCategory':
        setFilterCategory(value);
        break;
      case 'filterQuestionId':
        setFilterQuestionId(value);
        break;
      case 'sortBy':
        setSortBy(value);
        break;
      case 'sortOrder':
        setSortOrder(value);
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Viewer Banner */}
        <ViewerBanner />

        {/* Header with Stats */}
        <TaskManagerHeader
          stats={stats}
          currentFilter={filter}
          onFilterChange={setFilter}
        />

        {/* View Mode Toggle */}
        <div className="bg-white rounded-xl shadow-md p-3 mb-4">
          <div className="flex items-center gap-2 justify-center">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <List size={18} />
              <span>List View</span>
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                viewMode === 'calendar'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Calendar size={18} />
              <span>Calendar View</span>
            </button>
            <button
              onClick={() => setViewMode('gantt')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                viewMode === 'gantt'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <BarChart3 size={18} />
              <span>Gantt View</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <TaskFilters
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          filterValues={{
            filter,
            priorityFilter,
            activityTypeFilter,
            intangibleCapitalFilter,
            dueDateFilter,
            gapScoreFilter,
            filterCategory,
            filterQuestionId,
            assessmentTypeFilter,
            sortBy,
            sortOrder,
          }}
          onFilterChange={handleFilterChange}
          assessmentQuestions={assessmentQuestions}
          onResetFilters={handleResetFilters}
        />

        {/* Tasks Section */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {viewMode === 'list' ? 'Tasks' : viewMode === 'calendar' ? 'Calendar View' : 'Gantt Chart'}
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">{tasks.length} tasks</span>
              <div className="flex flex-col items-stretch gap-2">
                <div className="bg-gradient-to-r from-yellow-300 via-orange-300 to-yellow-400 px-4 py-2 rounded-lg shadow-md border-2 border-orange-400 animate-pulse">
                  <p className="text-xs font-bold text-gray-900 text-center whitespace-nowrap">
                    Don't See Any Tasks? Click the Load All button below.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleDownloadPDF}
                    disabled={downloadingPDF}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    title={downloadingPDF ? 'Generating PDF...' : 'Download tasks as PDF'}
                  >
                    {downloadingPDF ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <FileDown size={20} />
                        <span>Download PDF</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => fetchTasks(1, false, true, getCurrentServerFilters())}
                    disabled={loadingMore}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download size={20} />
                    Load All
                  </button>
                  {!isViewer && (
                    <button
                      onClick={() => {
                        fetchAssessmentQuestions();
                        setShowAddModal(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      <Plus size={20} />
                      Add Task
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* VIEW CONTENT */}
          {viewMode === 'list' && (
            <TaskList
              tasks={tasks}
              onTaskClick={setSelectedTask}
              onTaskDelete={handleTaskDelete}
            />
          )}

          {/* CALENDAR VIEW - Kept intact from original */}
          {viewMode === 'calendar' && (() => {
            const calendarData = getCalendarData();

            // Render task cell based on view type
            const renderTaskCell = (day, sizeClass, maxTasks) => (
              <div
                key={day.dateStr}
                className={`${sizeClass} p-2 rounded-lg border-2 transition cursor-pointer ${
                  selectedDate === day.dateStr
                    ? 'bg-indigo-100 border-indigo-500 shadow-lg'
                    : day.isToday
                    ? 'bg-blue-50 border-blue-400 shadow-md'
                    : day.isCurrentMonth
                    ? 'bg-white border-gray-200 hover:border-blue-300'
                    : 'bg-gray-50 border-gray-100'
                }`}
                onClick={() => setSelectedDate(day.dateStr)}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className={`text-sm font-semibold ${
                    selectedDate === day.dateStr ? 'text-indigo-700' :
                    day.isToday ? 'text-blue-600' : day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {calendarPeriod === '5day' || calendarPeriod === '7day'
                      ? day.date.toLocaleDateString('default', { weekday: 'short', month: 'short', day: 'numeric' })
                      : day.date.getDate()
                    }
                  </div>
                  {day.holidays && day.holidays.length > 0 && (
                    <div className="relative group/holiday">
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/holiday:opacity-100 transition pointer-events-none z-10">
                        {day.holidays.join(', ')}
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  {day.tasks.slice(0, maxTasks).map(task => (
                    <div
                      key={task.id}
                      className={`text-xs p-1.5 rounded cursor-pointer hover:shadow-md hover:scale-105 transition border-l-4 ${
                        task.status === 'completed' ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                        task.status === 'in_progress' ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' :
                        task.status === 'under_review' ? 'bg-purple-100 text-purple-800 hover:bg-purple-200' :
                        task.status === 'not_relevant' ? 'bg-gray-100 text-gray-500 hover:bg-gray-200' :
                        'bg-red-100 text-red-800 hover:bg-red-200'
                      } ${
                        task.priority === 'high' ? 'border-l-red-500' :
                        task.priority === 'medium' ? 'border-l-orange-500' :
                        'border-l-green-500'
                      }`}
                      title={task.title}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTask(task);
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <span className="truncate font-medium">{task.title}</span>
                      </div>
                    </div>
                  ))}
                  {day.tasks.length > maxTasks && (
                    <div className="text-xs text-gray-600 font-medium pl-1.5">
                      +{day.tasks.length - maxTasks} more
                    </div>
                  )}
                </div>
              </div>
            );

            return (
              <div className="space-y-4">
                {/* Calendar Controls */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl">
                  <button
                    onClick={() => navigateCalendar('prev')}
                    className="p-2 bg-white hover:bg-blue-50 rounded-lg shadow-sm transition min-h-[44px] min-w-[44px] flex items-center justify-center self-start"
                  >
                    <ChevronLeft size={24} className="text-blue-600" />
                  </button>

                  <div className="flex flex-col items-center gap-3 flex-1">
                    <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 text-center">{calendarData.title}</h3>
                      <button
                        onClick={() => {
                          setCalendarDate(new Date());
                          setSelectedDate(null);
                        }}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-md transition min-h-[44px]"
                      >
                        Today
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {['5day', '7day', 'month', '3month', '6month'].map(period => (
                        <button
                          key={period}
                          onClick={() => setCalendarPeriod(period)}
                          className={`px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition min-h-[44px] flex items-center ${
                            calendarPeriod === period
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'bg-white text-gray-700 hover:bg-blue-50'
                          }`}
                        >
                          <span className="hidden sm:inline">
                            {period === '5day' ? '5 Day Week' :
                             period === '7day' ? '7 Day Week' :
                             period === 'month' ? 'Month' :
                             period === '3month' ? '3 Months' :
                             '6 Months'}
                          </span>
                          <span className="sm:hidden">
                            {period === '5day' ? '5D' :
                             period === '7day' ? '7D' :
                             period === 'month' ? '1M' :
                             period === '3month' ? '3M' :
                             '6M'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={() => navigateCalendar('next')}
                    className="p-2 bg-white hover:bg-blue-50 rounded-lg shadow-sm transition min-h-[44px] min-w-[44px] flex items-center justify-center self-end sm:self-auto"
                  >
                    <ChevronRight size={24} className="text-blue-600" />
                  </button>
                </div>

                {/* 5-Day or 7-Day Week View */}
                {(calendarData.type === '5day' || calendarData.type === '7day') && (
                  <div className={`grid ${calendarData.type === '5day' ? 'grid-cols-5' : 'grid-cols-7'} gap-3`}>
                    {calendarData.days[0].map(day => renderTaskCell(day, 'min-h-[200px]', 8))}
                  </div>
                )}

                {/* Single Month View */}
                {calendarData.type === 'month' && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-7 gap-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center font-semibold text-gray-700 py-2">
                          {day}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-2">
                      {calendarData.weeks.map((week, weekIdx) => (
                        week.map((day, dayIdx) => (
                          <React.Fragment key={`${weekIdx}-${dayIdx}`}>
                            {renderTaskCell(day, 'min-h-[120px]', 3)}
                          </React.Fragment>
                        ))
                      ))}
                    </div>
                  </div>
                )}

                {/* 3-Month View */}
                {calendarData.type === '3month' && (
                  <div className="space-y-6">
                    {calendarData.months.map((monthData, monthIdx) => (
                      <div key={monthIdx} className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-lg font-bold text-gray-900 mb-3 text-center">{monthData.title}</h4>
                        <div className="grid grid-cols-7 gap-1 mb-2">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <div key={day} className="text-center font-semibold text-gray-600 text-xs py-1">
                              {day}
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                          {monthData.weeks.map((week, weekIdx) => (
                            week.map((day, dayIdx) => (
                              <React.Fragment key={`${monthIdx}-${weekIdx}-${dayIdx}`}>
                                {renderTaskCell(day, 'min-h-[80px]', 2)}
                              </React.Fragment>
                            ))
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 6-Month View */}
                {calendarData.type === '6month' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                    {calendarData.months.map((monthData, monthIdx) => (
                      <div key={monthIdx} className="bg-gray-50 p-3 rounded-lg">
                        <h4 className="text-base font-bold text-gray-900 mb-2 text-center">{monthData.title}</h4>
                        <div className="grid grid-cols-7 gap-0.5 mb-1">
                          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                            <div key={idx} className="text-center font-semibold text-gray-600 text-xs py-1">
                              {day}
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 gap-0.5">
                          {monthData.weeks.map((week, weekIdx) => (
                            week.map((day, dayIdx) => (
                              <React.Fragment key={`${monthIdx}-${weekIdx}-${dayIdx}`}>
                                {renderTaskCell(day, 'min-h-[60px]', 1)}
                              </React.Fragment>
                            ))
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Selected Date Tasks Detail */}
                {selectedDate && (() => {
                  // Parse the date string properly to avoid timezone issues
                  // selectedDate is in YYYY-MM-DD format
                  const [year, month, day] = selectedDate.split('-').map(Number);
                  const selectedDateObj = new Date(year, month - 1, day); // month is 0-indexed
                  const tasksForDate = tasks.filter(task => task.due_date === selectedDate);

                  return (
                    <div className="mt-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-gray-900">
                          Tasks for {selectedDateObj.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                        </h3>
                        <button
                          onClick={() => setSelectedDate(null)}
                          className="px-3 py-1.5 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition"
                        >
                          Close
                        </button>
                      </div>

                      {tasksForDate.length === 0 ? (
                        <p className="text-gray-600 text-center py-4">No tasks scheduled for this date</p>
                      ) : (
                        <div className="space-y-3">
                          {tasksForDate.map(task => (
                            <div
                              key={task.id}
                              className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition cursor-pointer border-l-4"
                              style={{
                                borderLeftColor:
                                  task.priority === 'high' ? '#ef4444' :
                                  task.priority === 'medium' ? '#f97316' :
                                  '#22c55e'
                              }}
                              onClick={() => setSelectedTask(task)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900 mb-1">{task.title}</h4>
                                  {task.description && (
                                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{task.description}</p>
                                  )}
                                  <div className="flex flex-wrap gap-2 items-center">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      task.status === 'completed' ? 'bg-green-100 text-green-800' :
                                      task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                      task.status === 'under_review' ? 'bg-purple-100 text-purple-800' :
                                      task.status === 'not_relevant' ? 'bg-gray-100 text-gray-600' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {task.status === 'not_started' ? 'Not Started' :
                                       task.status === 'in_progress' ? 'In Progress' :
                                       task.status === 'under_review' ? 'Under Review' :
                                       task.status === 'not_relevant' ? 'Not Relevant' :
                                       'Completed'}
                                    </span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      task.priority === 'high' ? 'bg-red-100 text-red-800' :
                                      task.priority === 'medium' ? 'bg-orange-100 text-orange-800' :
                                      'bg-green-100 text-green-800'
                                    }`}>
                                      {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
                                    </span>
                                    {task.question && (
                                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                                        {task.question.category_display}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })()}

          {/* GANTT VIEW */}
          {viewMode === 'gantt' && (
            <GanttChart
              tasks={getGanttData().tasks}
              onTaskUpdate={handleGanttTaskUpdate}
              onTaskClick={setSelectedTask}
            />
          )}
        </div>
      </div>

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreateTask={handleCreateTask}
        assessmentQuestions={assessmentQuestions}
      />

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleTaskUpdate}
        />
      )}
    </div>
  );
};

export default TaskManager;
